import { useAlipayDialogFlow } from '@cart-services/hooks/use-alipay-dialog-flow'
import { type AlipayDialogContext } from '@cart-services/hooks/use-alipay-dialog-flow-actions'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Drawer } from '@pets-ui/Drawer/Drawer'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogPaymentLeaveConfirm } from '../DialogPaymentLeaveConfirm/DialogPaymentLeaveConfirm'
import cartUiI18nInstance from '../i18n/i18nConfig'

const styles = stylex.create({
  mount: {
    position: 'relative',
    width: '100%',
  },
  mountHidden: {
    display: 'none',
  },
  loadingArea: {
    padding: '32px 0',
    display: 'flex',
    justifyContent: 'center',
  },
  drawerPaper: {
    borderRadius: '8px 8px 0px 0px',
  },
  cancelButton: {
    borderRadius: 8,
    height: 40,
  },
})

export interface DialogAlipayHkProps {
  context: AlipayDialogContext
  onErrorClose: () => void
  mountAlipayComponent: (context: AlipayDialogContext, containerId: string) => Promise<void>
  awaitFlowResult: (context: AlipayDialogContext) => Promise<void>
  cancelInFlight: (reason: string) => void
  /** 使用者確認離開付款時呼叫（離開確認框點「離開」）。 */
  onCancel?: () => void
  /**
   * 使用者確認離開時的關閉 handler。
   * 若提供，取代 onErrorClose 被 handleLeave 呼叫，避免觸發錯誤通知；
   * 未提供時 fallback 到 onErrorClose。
   */
  onCancelClose?: () => void
  /** mount 容器與下方按鈕間的額外間距（px），未提供時沿用第三方元件自帶的間距。 */
  actionsGap?: number
}

export const DialogAlipayHk = ({ context, onErrorClose, mountAlipayComponent, awaitFlowResult, cancelInFlight, onCancel, onCancelClose, actionsGap }: DialogAlipayHkProps) => {
  const responsiveIsMobile = useResponsive('mobile')
  /** 鎖定初次 render 的斷點：resize 跨 breakpoint 若重建 Drawer/Dialog subtree 會拆掉已掛載的 SDK 容器，此處無 remount 機制可回復。 */
  const [isMobile] = useState(responsiveIsMobile)
  const { isMounting, hasError, containerId, retry } = useAlipayDialogFlow({ context, mountAlipayComponent, awaitFlowResult, cancelInFlight })
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false)

  const handleCloseAttempt = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(true)
  })

  const handleStay = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(false)
  })

  const handleLeave = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(false)
    onCancel?.()
    cancelInFlight('user closed dialog')
    ;(onCancelClose ?? onErrorClose)()
  })

  const dialogInner = (
    <>
      <DialogHeader align="center" title={t('cartUi.alipayHkDialogTitle', { defaultValue: '使用 AlipayHK 完成付款' })} />
      <DialogContent>
        {isMounting
          ? (
            <div {...stylex.props(styles.loadingArea)}>
              <CircularProgress fontSize="32px" color={colors.grey500} />
            </div>
          )
          : (
            <VStack gap="16px">
              <Text variant="body" color={colors.grey700} align="center">
                {t('cartUi.alipayHkDialogDescription', { defaultValue: '請點擊下方按鈕前往 AlipayHK 完成付款。' })}
              </Text>
              {hasError && (
                <Text variant="subtitle" color={colors.red500} align="center">
                  {t('cartUi.paymentErrorGeneric', { defaultValue: '付款發生問題，請稍後再試或選擇其他付款方式。' })}
                </Text>
              )}
            </VStack>
          )}
      </DialogContent>
      <DialogActions flexDirection={hasError ? undefined : 'column'} gap={hasError ? undefined : (actionsGap ?? 0)}>
        <div {...stylex.props(styles.mount, (isMounting || hasError) && styles.mountHidden)}>
          <div
            id={containerId}
            data-testid={containerId}
          />
        </div>
        {!isMounting && (hasError
          ? (
            <>
              <Button variant="outlined" onClick={onErrorClose} rootStyle={styles.cancelButton} fullWidth>
                {t('cartUi.changePaymentMethod', { defaultValue: '更換付款方式' })}
              </Button>
              <Button onClick={retry} rootStyle={styles.cancelButton} fullWidth>
                {t('cartUi.retryPayment', { defaultValue: '重新再試' })}
              </Button>
            </>
          )
          : (
            <Button variant="outlined" color="grey" onClick={handleCloseAttempt} disabled={isLeaveConfirmOpen} rootStyle={styles.cancelButton} fullWidth>
              {t('cartUi.cancelPayment', { defaultValue: '取消付款' })}
            </Button>
          )
        )}
      </DialogActions>
    </>
  )

  return (
    <>
      {isMobile
        ? (
          <Drawer open direction="bottom" height="auto" xStyles={{ base: { paper: styles.drawerPaper } }}>
            {dialogInner}
          </Drawer>
        )
        : (
          <Dialog open fullWidth maxWidth={480} height="auto">
            {dialogInner}
          </Dialog>
        )}
      {isLeaveConfirmOpen && <DialogPaymentLeaveConfirm onStay={handleStay} onLeave={handleLeave} />}
    </>
  )
}
